import type { ManualSkill } from '@Types/skills';

// Also hardcoded in promptfoo/asserts/jevNoul.js. Kept as two literals, not
// one shared constant: this file is bundled by webpack for the browser, the
// other runs as a standalone Node script outside the build (same
// cross-boundary limitation as the comment on createSSEProcessor in
// src/utils/sseProcessor.ts) — a shared module would need a new
// webpack/tsconfig path alias, which is outside this refactor's scope.
// Keep both literals in sync if this URL ever changes.
const TYPESAFE_URL = 'https://api.typesafe.ai/v1/systemone';
const CONFIDENCE_THRESHOLD = 0.6;
const TIMEOUT_MS = 4000;
const NONE_OPTION = 'none';

interface ChoiceAnswer {
  choice: string;
  confidence: number;
}

/**
 * Prototype only: calls TypeSafe directly from the browser with a
 * REACT_APP_TYPESAFE_API_KEY, so the key is visible in the bundle/devtools.
 * Not safe to ship as-is; a real backend proxy should hold the key.
 */
export const routeSkill = async (
  input: string,
  skills: ManualSkill[],
): Promise<ManualSkill | null> => {
  const apiKey = process.env.REACT_APP_TYPESAFE_API_KEY;
  if (!apiKey || skills.length === 0) return null;

  const criteria: Record<string, string> = {
    [NONE_OPTION]: 'No listed skill is relevant to this message.',
  };
  skills.forEach(skill => {
    criteria[skill.skill_id] = skill.description;
  });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch(TYPESAFE_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
      body: JSON.stringify({
        state: input,
        model: 'jev-latest',
        questions: {
          skill: {
            type: 'choice',
            instructions: 'Which skill, if any, should handle this message?',
            criteria,
          },
        },
      }),
    });
    if (!response.ok) return null;

    const body: { answers?: { skill?: ChoiceAnswer } } = await response.json();
    const answer = body.answers?.skill;
    if (
      !answer ||
      answer.choice === NONE_OPTION ||
      answer.confidence < CONFIDENCE_THRESHOLD
    ) {
      return null;
    }
    return skills.find(skill => skill.skill_id === answer.choice) ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
};
